// MARK://dhk8/pos/features/kds/useTickets
// react-query over the kds tickets client. The board polls every few seconds (a
// fallback when @dockethub/realtime isn't connected) and action mutations apply an
// optimistic update before invalidating. Absorbed from `apps/dhk8-kds/src/hooks/useTickets.ts`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { KitchenTicketDto, KitchenTicketItemStatus } from '@dockethub/ui-restaurant'
import {
  listTickets,
  startTicket,
  readyTicket,
  bumpTicket,
  recallTicket,
  updateTicketItemStatus,
} from '../../lib/clients/kds.js'

export const POLL_MS = 5_000

export const ticketKeys = {
  all: ['kds', 'tickets'] as const,
  active: (stationId: string | null) => ['kds', 'tickets', 'active', stationId ?? 'all'] as const,
}

/** Live tickets for a station (or all) — PENDING|IN_PROGRESS|RECALLED, polled. */
export function useActiveTickets(stationId: string | null) {
  return useQuery({
    queryKey: ticketKeys.active(stationId),
    queryFn: ({ signal }) => listTickets({ active: true, ...(stationId ? { stationId } : {}), limit: 200 }, signal),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 2_000,
  })
}

export type TicketVerb = 'start' | 'ready' | 'bump' | 'recall'
const VERB_FN: Record<TicketVerb, (id: string) => Promise<KitchenTicketDto>> = {
  start: startTicket, ready: readyTicket, bump: bumpTicket, recall: recallTicket,
}
const VERB_NEXT: Record<TicketVerb, KitchenTicketDto['status']> = {
  start: 'IN_PROGRESS', ready: 'READY', bump: 'BUMPED', recall: 'RECALLED',
}

/** Mutate a ticket with optimistic UI: the targeted ticket flips status (and
 *  BUMPED tickets vanish) immediately; on error the snapshot is restored. */
export function useTicketAction(stationId: string | null) {
  const qc = useQueryClient()
  const key = ticketKeys.active(stationId)
  return useMutation({
    mutationFn: ({ id, verb }: { id: string; verb: TicketVerb }) => VERB_FN[verb](id),
    onMutate: async ({ id, verb }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<KitchenTicketDto[]>(key)
      if (prev) {
        const nextStatus = VERB_NEXT[verb]
        const optimistic = prev
          .map((t) => (t.id === id ? { ...t, status: nextStatus } : t))
          .filter((t) => t.status !== 'BUMPED')
        qc.setQueryData<KitchenTicketDto[]>(key, optimistic)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ticketKeys.all }) },
  })
}

/** Marca el estado de UN ítem dentro de un ticket (PREPARING / READY) — el cocinero
 *  avanza item por item en lugar del ticket completo. Optimista: el ítem flipea su
 *  status al instante; en error se restaura el snapshot. La mutación devuelve el
 *  ticket completo, así que reusamos ese resultado para refrescar la fila. */
export function useItemStatusAction(stationId: string | null) {
  const qc = useQueryClient()
  const key = ticketKeys.active(stationId)
  return useMutation({
    mutationFn: ({ ticketId, itemId, status }: { ticketId: string; itemId: string; status: KitchenTicketItemStatus }) =>
      updateTicketItemStatus(ticketId, itemId, status),
    onMutate: async ({ ticketId, itemId, status }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<KitchenTicketDto[]>(key)
      if (prev) {
        const optimistic = prev.map((t) =>
          t.id === ticketId
            ? { ...t, items: t.items.map((it) => (it.id === itemId ? { ...it, status } : it)) }
            : t,
        )
        qc.setQueryData<KitchenTicketDto[]>(key, optimistic)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ticketKeys.all }) },
  })
}
